/**
 * Менеджер блокировок для системы транзакций
 * Обеспечивает изоляцию и предотвращение конфликтов
 */

import { EventEmitter } from 'eventemitter3';
import { LockManager, TransactionEventType, TransactionEventData } from '../types/transaction-interfaces';

/**
 * Информация о блокировке
 */
interface LockInfo {
  elementId: string;
  transactionId: string;
  lockType: 'read' | 'write';
  timestamp: number;
  expiresAt?: number;
}

/**
 * Граф зависимостей для обнаружения взаимоблокировок
 */
interface DependencyGraph {
  [transactionId: string]: Set<string>;
}

/**
 * Реализация менеджера блокировок
 */
export class LockManagerImpl extends EventEmitter implements LockManager {
  private locks: Map<string, LockInfo> = new Map();
  private waitingQueue: Map<string, Array<{
    transactionId: string;
    lockType: 'read' | 'write';
    resolve: (success: boolean) => void;
    timestamp: number;
  }>> = new Map();
  
  private defaultTimeout: number = 30000; // 30 секунд
  private deadlockCheckInterval: number = 5000; // 5 секунд
  private deadlockCheckTimer?: NodeJS.Timeout;

  constructor() {
    super();
    this.startDeadlockDetection();
  }

  /**
   * Получение блокировки на элемент
   */
  async acquireLock(elementId: string, transactionId: string, lockType: 'read' | 'write'): Promise<boolean> {
    const existingLock = this.locks.get(elementId);

    // Если блокировки нет, создаем новую
    if (!existingLock) {
      return this.createLock(elementId, transactionId, lockType);
    }

    // Если блокировка принадлежит той же транзакции
    if (existingLock.transactionId === transactionId) {
      // Обновляем тип блокировки если нужно (read -> write)
      if (existingLock.lockType === 'read' && lockType === 'write') {
        existingLock.lockType = 'write';
        existingLock.timestamp = Date.now();
      }
      return true;
    }

    // Проверяем совместимость блокировок
    const compatible = this.areLocksCompatible(existingLock.lockType, lockType);
    
    if (compatible && lockType === 'read') {
      // Для read-блокировок можем создать разделяемую блокировку
      return this.createSharedLock(elementId, transactionId, lockType);
    }

    // Блокировки несовместимы - добавляем в очередь ожидания
    return this.waitForLock(elementId, transactionId, lockType);
  }

  /**
   * Освобождение блокировки
   */
  async releaseLock(elementId: string, transactionId: string): Promise<boolean> {
    const lock = this.locks.get(elementId);
    
    if (!lock || lock.transactionId !== transactionId) {
      return false;
    }

    this.locks.delete(elementId);

    this.emitEvent({
      type: TransactionEventType.LOCK_RELEASED,
      timestamp: Date.now(),
      transactionId,
      elementId
    });

    // Обрабатываем очередь ожидания
    await this.processWaitingQueue(elementId);

    return true;
  }

  /**
   * Освобождение всех блокировок транзакции
   */
  async releaseAllLocks(transactionId: string): Promise<number> {
    let releasedCount = 0;
    const locksToRelease: string[] = [];

    // Собираем все блокировки транзакции
    for (const [elementId, lock] of this.locks) {
      if (lock.transactionId === transactionId) {
        locksToRelease.push(elementId);
      }
    }

    // Освобождаем блокировки
    for (const elementId of locksToRelease) {
      if (await this.releaseLock(elementId, transactionId)) {
        releasedCount++;
      }
    }

    return releasedCount;
  }

  /**
   * Проверка блокировки элемента
   */
  isLocked(elementId: string): boolean {
    return this.locks.has(elementId);
  }

  /**
   * Получение информации о блокировке
   */
  getLockInfo(elementId: string): {
    locked: boolean;
    transactionId?: string;
    lockType?: 'read' | 'write';
    timestamp?: number;
  } {
    const lock = this.locks.get(elementId);
    
    if (!lock) {
      return { locked: false };
    }

    return {
      locked: true,
      transactionId: lock.transactionId,
      lockType: lock.lockType,
      timestamp: lock.timestamp
    };
  }

  /**
   * Обнаружение взаимоблокировок
   */
  detectDeadlocks(): string[][] {
    const graph = this.buildDependencyGraph();
    const deadlocks: string[][] = [];
    const visited = new Set<string>();
    const recursionStack = new Set<string>();

    // Поиск циклов в графе зависимостей
    for (const transactionId of Object.keys(graph)) {
      if (!visited.has(transactionId)) {
        const cycle = this.findCycle(transactionId, graph, visited, recursionStack, []);
        if (cycle.length > 0) {
          deadlocks.push(cycle);
        }
      }
    }

    if (deadlocks.length > 0) {
      this.emitEvent({
        type: TransactionEventType.DEADLOCK_DETECTED,
        timestamp: Date.now(),
        transactionId: deadlocks[0][0],
        metadata: { deadlocks }
      });
    }

    return deadlocks;
  }

  /**
   * Принудительное снятие блокировок
   */
  async forceReleaseLocks(elementIds: string[]): Promise<number> {
    let releasedCount = 0;

    for (const elementId of elementIds) {
      const lock = this.locks.get(elementId);
      if (lock) {
        this.locks.delete(elementId);
        releasedCount++;

        this.emitEvent({
          type: TransactionEventType.LOCK_RELEASED,
          timestamp: Date.now(),
          transactionId: lock.transactionId,
          elementId,
          metadata: { forced: true }
        });

        // Обрабатываем очередь ожидания
        await this.processWaitingQueue(elementId);
      }
    }

    return releasedCount;
  }

  /**
   * Создание новой блокировки
   */
  private createLock(elementId: string, transactionId: string, lockType: 'read' | 'write'): boolean {
    const lockInfo: LockInfo = {
      elementId,
      transactionId,
      lockType,
      timestamp: Date.now(),
      expiresAt: Date.now() + this.defaultTimeout
    };

    this.locks.set(elementId, lockInfo);

    this.emitEvent({
      type: TransactionEventType.LOCK_ACQUIRED,
      timestamp: Date.now(),
      transactionId,
      elementId,
      metadata: { lockType }
    });

    return true;
  }

  /**
   * Создание разделяемой блокировки (для read-операций)
   */
  private createSharedLock(elementId: string, transactionId: string, lockType: 'read' | 'write'): boolean {
    // В упрощенной реализации просто создаем обычную блокировку
    // В полной реализации здесь была бы поддержка множественных read-блокировок
    return this.createLock(elementId, transactionId, lockType);
  }

  /**
   * Ожидание освобождения блокировки
   */
  private async waitForLock(elementId: string, transactionId: string, lockType: 'read' | 'write'): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.waitingQueue.has(elementId)) {
        this.waitingQueue.set(elementId, []);
      }

      const queue = this.waitingQueue.get(elementId)!;
      queue.push({
        transactionId,
        lockType,
        resolve,
        timestamp: Date.now()
      });

      // Устанавливаем таймаут
      setTimeout(() => {
        const index = queue.findIndex(item => item.transactionId === transactionId);
        if (index >= 0) {
          queue.splice(index, 1);
          resolve(false);
        }
      }, this.defaultTimeout);
    });
  }

  /**
   * Обработка очереди ожидания после освобождения блокировки
   */
  private async processWaitingQueue(elementId: string): Promise<void> {
    const queue = this.waitingQueue.get(elementId);
    if (!queue || queue.length === 0) {
      return;
    }

    // Берем первый запрос из очереди
    const nextRequest = queue.shift()!;
    
    // Пытаемся создать блокировку
    const success = this.createLock(elementId, nextRequest.transactionId, nextRequest.lockType);
    nextRequest.resolve(success);

    // Если остались запросы и новая блокировка совместима, обрабатываем их
    if (success && nextRequest.lockType === 'read' && queue.length > 0) {
      const compatibleRequests = queue.filter(req => req.lockType === 'read');
      for (const request of compatibleRequests) {
        const index = queue.indexOf(request);
        if (index >= 0) {
          queue.splice(index, 1);
          const lockCreated = this.createSharedLock(elementId, request.transactionId, request.lockType);
          request.resolve(lockCreated);
        }
      }
    }
  }

  /**
   * Проверка совместимости блокировок
   */
  private areLocksCompatible(existingType: 'read' | 'write', requestedType: 'read' | 'write'): boolean {
    // read-read совместимы, остальные комбинации - нет
    return existingType === 'read' && requestedType === 'read';
  }

  /**
   * Построение графа зависимостей для обнаружения взаимоблокировок
   */
  private buildDependencyGraph(): DependencyGraph {
    const graph: DependencyGraph = {};

    // Инициализируем граф
    for (const lock of this.locks.values()) {
      if (!graph[lock.transactionId]) {
        graph[lock.transactionId] = new Set();
      }
    }

    // Добавляем зависимости из очереди ожидания
    for (const [elementId, queue] of this.waitingQueue) {
      const currentLock = this.locks.get(elementId);
      if (currentLock) {
        for (const waitingRequest of queue) {
          if (!graph[waitingRequest.transactionId]) {
            graph[waitingRequest.transactionId] = new Set();
          }
          graph[waitingRequest.transactionId].add(currentLock.transactionId);
        }
      }
    }

    return graph;
  }

  /**
   * Поиск цикла в графе зависимостей
   */
  private findCycle(
    transactionId: string,
    graph: DependencyGraph,
    visited: Set<string>,
    recursionStack: Set<string>,
    path: string[]
  ): string[] {
    visited.add(transactionId);
    recursionStack.add(transactionId);
    path.push(transactionId);

    const dependencies = graph[transactionId] || new Set();
    for (const dependencyId of dependencies) {
      if (!visited.has(dependencyId)) {
        const cycle = this.findCycle(dependencyId, graph, visited, recursionStack, path);
        if (cycle.length > 0) {
          return cycle;
        }
      } else if (recursionStack.has(dependencyId)) {
        // Найден цикл
        const cycleStart = path.indexOf(dependencyId);
        return path.slice(cycleStart);
      }
    }

    recursionStack.delete(transactionId);
    path.pop();
    return [];
  }

  /**
   * Запуск периодической проверки взаимоблокировок
   */
  private startDeadlockDetection(): void {
    this.deadlockCheckTimer = setInterval(() => {
      const deadlocks = this.detectDeadlocks();
      if (deadlocks.length > 0) {
        console.warn('Обнаружены взаимоблокировки:', deadlocks);
        // В реальной реализации здесь была бы логика разрешения взаимоблокировок
      }
    }, this.deadlockCheckInterval);
  }

  /**
   * Остановка проверки взаимоблокировок
   */
  private stopDeadlockDetection(): void {
    if (this.deadlockCheckTimer) {
      clearInterval(this.deadlockCheckTimer);
      this.deadlockCheckTimer = undefined;
    }
  }

  /**
   * Очистка ресурсов
   */
  destroy(): void {
    this.stopDeadlockDetection();
    this.locks.clear();
    this.waitingQueue.clear();
    this.removeAllListeners();
  }

  /**
   * Получение статистики блокировок
   */
  getStats(): {
    totalLocks: number;
    readLocks: number;
    writeLocks: number;
    waitingRequests: number;
    oldestLock?: LockInfo;
  } {
    const locks = Array.from(this.locks.values());
    const readLocks = locks.filter(lock => lock.lockType === 'read').length;
    const writeLocks = locks.filter(lock => lock.lockType === 'write').length;
    
    let waitingRequests = 0;
    for (const queue of this.waitingQueue.values()) {
      waitingRequests += queue.length;
    }

    const oldestLock = locks.reduce((oldest, current) => {
      return !oldest || current.timestamp < oldest.timestamp ? current : oldest;
    }, undefined as LockInfo | undefined);

    return {
      totalLocks: locks.length,
      readLocks,
      writeLocks,
      waitingRequests,
      oldestLock
    };
  }

  /**
   * Генерация события транзакционной системы
   */
  private emitEvent(eventData: TransactionEventData): void {
    this.emit('transaction:event', eventData);
    this.emit(eventData.type, eventData);
  }
} 